import React from "react"
import { Helmet } from "react-helmet"
import { useDefaultAuthor } from "../hooks/use-authors"
import { useSiteMetadata } from "../hooks/use-site-metadata"
import { getSchema } from "../utils/schema-org"

/**
 * @param {string} usernameOrUrl
 */
function getTwitterUsername(usernameOrUrl = "") {
  let username = usernameOrUrl
  const isUrl = usernameOrUrl.startsWith("http")

  if (isUrl) {
    const { pathname } = new URL(usernameOrUrl)
    username = pathname
  }

  return username.replace(/[@/]/g, "")
}

function getTwitterCardMeta({
  cardType = "summary",
  title,
  description,
  imageUrl,
  imageAltText,
  siteAccount,
  creatorAccount,
}) {
  const meta = [
    {
      name: "twitter:title",
      content: title,
    },
    {
      name: "twitter:description",
      content: description,
    },
    {
      name: "twitter:card",
      content: cardType,
    },
  ]

  if (imageUrl) {
    meta.push({
      name: "twitter:image",
      content: imageUrl,
    })
  }

  if (imageAltText) {
    meta.push({
      name: "twitter:image:alt",
      content: imageAltText,
    })
  }

  if (siteAccount) {
    meta.push({
      name: "twitter:site",
      content: getTwitterUsername(siteAccount),
    })
  }

  if (creatorAccount) {
    meta.push({
      name: "twitter:creator",
      content: getTwitterUsername(creatorAccount),
    })
  }

  return meta
}

export const SEO = ({
  description,
  lang = "en",
  meta = [],
  title,
  slug,
  schemaOrgType,
  datePublished,
  dateModified,
  author,
  logoUrl,
  imageUrl,
  imageAltText,
}) => {
  const {
    siteUrl,
    pageUrl,
    social,
    title: siteName,
    description: siteDescription,
    logoUrl: defaultLogoUrl,
  } = useSiteMetadata(slug)

  const metaAuthor = useDefaultAuthor(author)

  const metaTitle = title || siteName
  const metaDescription = description || siteDescription
  const metaLogoUrl = logoUrl || defaultLogoUrl
  const metaImageUrl = imageUrl || metaLogoUrl

  const schema = getSchema({
    type: schemaOrgType,
    description: metaDescription,
    logoUrl: metaLogoUrl,
    author: metaAuthor,
    title,
    siteUrl,
    siteName,
    pageUrl,
    imageUrl,
    dateModified,
    datePublished,
  })

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={metaTitle}
      titleTemplate={title ? `%s | ${siteName}` : `%s`}
      meta={[
        {
          name: "author",
          content: metaAuthor.displayName,
        },
        {
          name: "description",
          content: metaDescription,
        },
        {
          property: "og:url",
          content: pageUrl,
        },
        {
          property: "og:title",
          content: metaTitle,
        },
        {
          property: "og:description",
          content: metaDescription,
        },
        {
          property: "og:type",
          content: "website",
        },
        {
          property: "og:image",
          content: metaImageUrl,
        },
        ...getTwitterCardMeta({
          cardType: imageUrl ? "summary_large_image" : "summary",
          title: metaTitle,
          description: metaDescription,
          imageUrl: metaImageUrl,
          siteAccount: social?.twitter,
          creatorAccount: metaAuthor.social?.twitter,
          imageAltText,
        }),
        ...meta,
      ]}
    >
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}
